import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery, baseApi } from '@store/rtk/base';
import { createQueryString } from '@apiService/ApiHelper';
import { decryptPayload } from '@helper/index';

// define a service using a base URL and expected endpoints
export const bannersApi = baseApi.injectEndpoints({
  overrideExisting: false,

  endpoints: (builder) => ({
    // get banners api endpoint
    getBanners: builder.query({
      providesTags: ['BANNERS'],

      // api endpoint query setup
      query: (QueryArg) => {
        let payload = {};
        let cinema = JSON.parse(localStorage.getItem(`selected_cinema`));
        if (cinema) payload.cinema_id = cinema.cinema_id;

        let city = JSON.parse(localStorage.getItem(`selected_city`));
        if (city) payload.city_id = city?.city_id;

        return {
          url: `api/cinema/banners${createQueryString(payload)}`,

          // query responses validation for success or failure, our API always returns a 200, but sets an `status` property for success or failure
          validateStatus: (response, result) => {
            return response?.status === 200 && result?.status === true;
          },
        };
      },

      // customizing query responses with transformResponse
      transformResponse: (data, meta, arg) => {
        const keys = {
          key: process.env.REACT_APP_CRYPTO_KEY,
          iv: process.env.REACT_APP_CRYPTO_IV,
        };

        let decryptedPayload = decryptPayload(data.encryptedData, keys);
        let parsedData = decryptedPayload;

        try {
          parsedData = JSON.parse(decryptedPayload);
        } catch (error) {
          console.log('error in rtk parsing', error);
          parsedData = decryptedPayload;
        }
        data = parsedData;
        let filtered = data?.Records.map((b) => {
          const {
            movie_id,
            now_showing_count,
            now_demand_count,
            movie_banner_type_id,
          } = b;

          const isGenericBanner = !movie_id;
          const movie_banner_type_ids = (movie_banner_type_id || '')
            .split(',')
            .map((x) => x.trim())
            .filter(Boolean);

          let isNowShowingBannerFromSchedule = false;
          let isOnDemandBannerFromSchedule = false;
          let isUpcomingBannerFromSchedule = false;

          let isNowShowingBannerFromBannerType = false;
          let isOnDemandBannerFromBannerType = false;
          let isUpcomingBannerFromBannerType = false;

          if (!isGenericBanner) {
            switch (true) {
              case movie_banner_type_ids.includes('3'):
                isUpcomingBannerFromBannerType = true;
                break;
              case movie_banner_type_ids.includes('2'):
                isOnDemandBannerFromBannerType = true;
                break;
              case movie_banner_type_ids.includes('1'):
                isNowShowingBannerFromBannerType = true;
                break;
            }

            isNowShowingBannerFromSchedule = now_showing_count > 0;
            isOnDemandBannerFromSchedule = now_demand_count > 0;
            isUpcomingBannerFromSchedule =
              !isNowShowingBannerFromSchedule && !isOnDemandBannerFromSchedule;
          }

          return {
            ...b,
            isGenericBanner,
            // isNowShowingBannerFromSchedule,
            // isOnDemandBannerFromSchedule,
            // isUpcomingBannerFromSchedule,
            // isNowShowingBannerFromBannerType,
            // isOnDemandBannerFromBannerType,
            // isUpcomingBannerFromBannerType,
            isNowShowing:
              isNowShowingBannerFromSchedule ?? isNowShowingBannerFromBannerType,
            isOnDemand:
              isOnDemandBannerFromSchedule ?? isOnDemandBannerFromBannerType,
            isUpcoming:
              isUpcomingBannerFromSchedule ?? isUpcomingBannerFromBannerType,
          };
        });

        return filtered;
      },

      onQueryStarted: async (
        QueryArg,
        {
          dispatch,
          getState,
          extra,
          requestId,
          queryFulfilled,
          getCacheEntry,
          updateCachedData, // available for query endpoints only
        },
      ) => {
        // `onStart` side-effect
        dispatch({ type: 'Fetching Banners' });

        try {
          const { data, meta } = await queryFulfilled;
          // `onSuccess` side-effect
          dispatch({ type: 'Banners received!' });
        } catch (err) {
          // `onError` side-effect
          dispatch({ type: 'Error fetching Banners!' });
        }
      },

      onCacheEntryAdded: async (
        QueryArg,
        {
          dispatch,
          getState,
          extra,
          requestId,
          cacheEntryRemoved,
          cacheDataLoaded,
          getCacheEntry,
          updateCachedData, // available for query endpoints only
        },
      ) => {},
    }),
  }),
});

// Export hooks for usage in functional components, which are auto-generated based on the defined endpoints
export const { useGetBannersQuery } = bannersApi;
